import React, { useEffect, useState, useCallback } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess } from "chess.js";
import ControlPanel from './ControlPanel';
import '../App.css';

export default function ChessBoard() {
    const [game, setGame] = useState(new Chess());
    const [active, setActive] = useState(false);
    const [userColor, setUserColor] = useState("w");
    const [orientation, setOrientation] = useState("white");
    const [savedPgn, setSavedPgn] = useState(null);
    const [source, setSource] = useState("lichess");
    const [ratings, setRatings] = useState(["2200", "2500"]);
    const [speeds, setSpeeds] = useState(["blitz", "rapid", "classical"]);
    const [futureMoves, setFutureMoves] = useState([]);
    const [selectedSquare, setSelectedSquare] = useState(null);
    const [selectedSquareStyling, setselectedSquareStyling] = useState({})
    const [highlightedSquares, setHighlightedSquares] = useState({});
    const [hoverSquare, setHoverSquare] = useState({})

    const highlightSquareStyle = {
        backgroundColor: 'rgba(255, 145, 0, 0.4)',
    };

    useEffect(() => {
        if (active && game.turn() !== userColor) {
            makeRandomMove();
        }
        setSelectedSquare(null);
        setHighlightedSquares(getLastMove());
    }, [active, game]);

    useEffect(() => {
        if (selectedSquare) {
            setHighlightedSquares(getLegalMoves(selectedSquare));
            const styling = {}
            styling[selectedSquare] = highlightSquareStyle;
            setselectedSquareStyling(styling);
        } else {
            setHighlightedSquares(getLastMove());
            setselectedSquareStyling({})
        }
    }, [selectedSquare]);

    const makeAMove = (move) => {
        const gameCopy = getGameCopy();
        try {
            const result = gameCopy.move(move);
            setFutureMoves([]);
            if (!result) return null;
            setGame(gameCopy);
            return result;
        } catch {
            return null;
        }
    };

    const getLastMove = () => {
        const history = game.history({ verbose: true });
        const highlights = {}
        if (history && history.length > 0) {
            const move = history[history.length - 1];
            const style = {
                backgroundColor: 'rgba(255, 145, 0, 0.4)'
            };
            highlights[move.from] = style;
            highlights[move.to] = style;
        }
        return highlights;
    }

    const getLegalMoves = (square) => {
        const moves = game.moves({ square, verbose: true });
        const highlights = {};
        const capture = {
            outline: '8px solid rgba(255, 145, 0, 0.4)',
            outlineOffset: '-8px'
        }
        const movement = {
            background: 'radial-gradient(circle, rgba(255, 145, 0, 0.4) 25%, transparent 25%)',
            borderRadius: "50%",
        }
        moves.forEach((move) => {
          highlights[move.to] = game.get(move.to) && game.get(move.to).color !== game.turn()
            ? capture
            : movement;
        });
        return {
            ...getLastMove(),
            ...highlights,
        };
    }

    const getGameCopy = () => {
        const gameCopy = getNewChess();
        if (game.history().length > 0)
            gameCopy.loadPgn(game.pgn());
        return gameCopy;
    }

    const getNewChess = () => {
        const newChess = new Chess();
        newChess.setHeader("Event", "ChessAssess Opening Trainer");
        newChess.setHeader("Site", "chessassess.com");

        var today = new Date();
        var dd = String(today.getDate()).padStart(2, '0');
        var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
        var yyyy = today.getFullYear();

        today = yyyy + '-' + mm + '-' + dd;
        newChess.setHeader("Date", today);

        if (userColor === 'w') {
            newChess.setHeader("White", "User");
            newChess.setHeader("Black", "Lichess Opening Book");
        }
        else {
            newChess.setHeader("Black", "User");
            newChess.setHeader("White", "Lichess Opening Book");
        }

        return newChess;
    }

    const makeRandomMove = async () => {
        const fen = game.fen();
        const params = new URLSearchParams({
            variant: 'standard',
            fen: fen,
        });

        if (source !== "masters") {
            if (ratings.length > 0) params.set("ratings", ratings.join(","));
            if (speeds.length > 0) params.set("speeds", speeds.join(","));
        }

        try {
            const response = await fetch(`/api/opening-explorer?source=${source}&${params}`);
            const data = await response.json();
            const moves = data.moves;
            if (!moves || moves.length === 0) {
                setActive(false);
                return;
            }

            const total = moves.reduce((sum, m) => sum + m.white + m.black + m.draws, 0);
            let rand = Math.random() * total;

            for (let move of moves) {
                const weight = move.white + move.black + move.draws;
                if (rand < weight) {
                    const result = makeAMove(move.san);
                    if (!result) {
                        console.log("bad result - " + move.san);
                        setActive(false);
                    }
                    break;
                }
                rand -= weight;
            }
        } catch (error) {
            console.error('Lichess fetch error:', error);
            setActive(false);
        }
    };

    const handleStepBack = useCallback(() => {
        setActive(false);
        const gameCopy = getGameCopy();
        const move = gameCopy.undo();
        if (move) {
            setFutureMoves(prev => [move.san, ...prev]);
            setGame(gameCopy);
        }
    }, [game]);
    
    const handleStepForward = useCallback(() => {
        setActive(false);
        setFutureMoves(prev => {
            if (prev.length === 0) return prev;
            const gameCopy = getGameCopy();
            const [nextMove, ...rest] = prev;
            const move = gameCopy.move(nextMove);
            if (move) {
                setGame(gameCopy);
                return rest;
            }
            return prev;
        });
    }, [game]);

    const onDrop = (sourceSquare, targetSquare) => {
        const move = makeAMove({ from: sourceSquare, to: targetSquare, promotion: "q" });
        return move !== null;
    };

    const onSquareClick = (square, piece) => {
        if (selectedSquare) {
            if (!makeAMove({ from: selectedSquare, to: square, promotion: "q" })) {
                setSelectedSquare(square);
            }
        } else {
            setSelectedSquare(square);
        }
    }
    
    const onMouseOverSquare = (square) => {
        const highlight = {}
        highlight[square] = highlightSquareStyle;
        const moves = getLegalMoves(selectedSquare);
        if (selectedSquare && square in highlightedSquares && square in moves) {
            setHoverSquare(highlight)
        }
    }

    const onMouseOutSquare = (square) => {
        setHoverSquare({});
    }

    const toggleRating = (rating) => {
        setRatings(prev => prev.includes(rating) ? prev.filter(r => r !== rating) : [...prev, rating]);
    };

    const toggleSpeed = (speed) => {
        setSpeeds(prev => prev.includes(speed) ? prev.filter(s => s !== speed) : [...prev, speed]);
    };

    const handleReset = () => {
        setActive(false);
        setFutureMoves([]);
        if (savedPgn && game.pgn() !== savedPgn) {
            try {
                const newChess = getNewChess();
                newChess.loadPgn(savedPgn);
                setGame(newChess);
            }
            catch {
                setGame(getNewChess());
            }
        } else {
            setGame(getNewChess());
        }
    };

    const handleSave = () => {
        setSavedPgn(game.pgn());
    };

    const pgnMoves = game.history().join("_");
    const lichessUrl = `https://lichess.org/analysis/pgn/${pgnMoves}`;

    return (
        <div className="main-container">
          <div className="content-wrapper">
            <div className="chess-section"
                style={game.pgn() == savedPgn ? {outline: '4px solid var(--accent)'} : {}}
            >
                <div className="board-wrapper"
                >
                    <Chessboard
                        position={game.fen()}
                        onPieceDrop={onDrop}
                        onPieceDragBegin={(piece, square) => {setSelectedSquare(square)}}
                        onSquareClick={onSquareClick}
                        onMouseOverSquare={onMouseOverSquare}
                        onMouseOutSquare={onMouseOutSquare}
                        boardOrientation={orientation}
                        customLightSquareStyle={{ backgroundColor: '#ba9a72' }}
                        customDarkSquareStyle={{ backgroundColor: '#8a7155' }}
                        customSquareStyles={{
                            ...highlightedSquares,
                            ...hoverSquare,
                            ...selectedSquareStyling,
                          }}
                        customDropSquareStyle={highlightSquareStyle}
                    />
                </div>
            </div>
      
            <div className="sidebar-section">
              <ControlPanel
                userColor={userColor}
                setUserColor={(color) => {
                  setUserColor(color);
                  setOrientation(color === "w" ? "white" : "black");
                }}
                active={active}
                setActive={setActive}
                handleSave={handleSave}
                handleReset={handleReset}
                source={source}
                setSource={setSource}
                toggleRating={toggleRating}
                ratings={ratings}
                toggleSpeed={toggleSpeed}
                speeds={speeds}
              />
      
              <div>
                <label>
                  <strong>FEN:</strong><br />
                  <input type="text" readOnly value={game.fen()} style={{ width: "100%" }} />
                </label>
                <label>
                  <strong>PGN:</strong><br />
                  <textarea readOnly value={game.pgn()} style={{ width: "100%", height: "4rem" }} />
                </label>
                <a href={lichessUrl} target="_blank" rel="noopener noreferrer">
                  <button>Analyze on Lichess</button>
                </a>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                    <button onClick={handleStepBack}>&larr;</button>
                    <button onClick={handleStepForward}>&rarr;</button>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      );
}